import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Receipt, Trash2 } from 'lucide-react';
import {
  formatCents,
  formatIban,
  isValidIban,
  type InvoiceFormat,
  type InvoiceTemplate,
  type Lease,
} from '@vuokramani/shared';
import { Section, StatusStrip, pageStyles as ps } from '../../components/page.tsx';
import {
  Alert,
  Button,
  ChoiceGroup,
  Dialog,
  DialogActions,
  EmptyState,
  Notice,
  Panel,
  SelectField,
  Splash,
  TextField,
  Segmented,
} from '../../components/ui.tsx';
import { ApiError, errorKey } from '../../lib/api.ts';
import { useMe } from '../../lib/auth.ts';
import { useExpenses } from '../../lib/expenses.ts';
import {
  addMonths,
  monthLabel,
  useCreateTemplate,
  useDeleteTemplate,
  useUpdateTemplate,
  useInvoices,
  useInvoiceTemplates,
  useSettleOverdue,
} from '../../lib/invoices.ts';
import {
  activeLease,
  fileUrl,
  useLeases,
  useOccupants,
  useUploadFiles,
} from '../../lib/tenancy.ts';
import {
  InvoiceList,
  LinesEditor,
  parseLines,
  toDraft,
  YearSwitch,
  type DraftLine,
} from '../shared/Invoices.tsx';
import s from '../shared/invoices.module.css';
import { ExpensesSection, RecurringExpenses } from './Expenses.tsx';
import { TaxNote } from '../shared/TaxNote.tsx';
import { useCurrentProperty } from './PropertyLayout.tsx';

/** Asunnon Talous-välilehti: vuokralaskut vuosittain ja laskupohjat. */
export function LandlordFinanceTab() {
  const { t, i18n } = useTranslation();
  const p = useCurrentProperty();
  const [year, setYear] = useState(new Date().getFullYear());
  const { data: leases, isLoading: leasesLoading } = useLeases(p.id);
  const { data: invoices, isLoading } = useInvoices(p.id, year);
  const { data: expenseData, isLoading: expensesLoading } = useExpenses(p.id, year);
  const lease = activeLease(leases);
  const settle = useSettleOverdue(p.id, lease?.id ?? '');
  const [confirmSettle, setConfirmSettle] = useState(false);
  // Laskut tai kulut kerrallaan; valinta osoitteessa (?osa=kulut), jotta se säilyy.
  const [params, setParams] = useSearchParams();
  const part = params.get('osa') === 'kulut' ? 'expenses' : 'invoices';

  if (leasesLoading || isLoading || expensesLoading) return <Splash />;
  const list = invoices ?? [];
  const money = (c: number) => formatCents(c, i18n.language);
  const billed = list.reduce((n, i) => n + i.totalCents, 0);
  const paid = list.reduce((n, i) => n + i.paidCents, 0);
  const totals = expenseData?.totals ?? { incomeCents: 0, expenseCents: 0 };
  const share = p.myOwnershipBp !== null && p.myOwnershipBp < 10000 ? p.myOwnershipBp / 100 : null;
  const overdue = list.filter((i) => i.status === 'overdue' && i.leaseId === lease?.id);

  return (
    <div className={ps.stack}>
      <Section
        title={t('finance.yearTitle', { year })}
        action={<YearSwitch year={year} onChange={setYear} />}
      >
        <StatusStrip
          items={[
            { label: t('finance.income'), value: money(totals.incomeCents) },
            { label: t('finance.expenses'), value: money(totals.expenseCents) },
            {
              label: t('finance.result'),
              value: money(totals.incomeCents - totals.expenseCents),
            },
            {
              label: t('invoices.openAmount'),
              value: money(billed - paid),
              muted: billed === paid,
            },
          ]}
          note={
            share !== null
              ? t('finance.noteShare', { share: share.toLocaleString(i18n.language) })
              : t('finance.note')
          }
        />
      </Section>

      <Segmented
        label={t('finance.partLabel')}
        value={part}
        options={[
          { value: 'invoices', label: t('invoices.title'), count: list.length },
          {
            value: 'expenses',
            label: t('expenses.title'),
            count: expenseData?.expenses.length ?? 0,
          },
        ]}
        onChange={(v) => setParams(v === 'expenses' ? { osa: 'kulut' } : {}, { replace: true })}
      />

      {part === 'invoices' && (
        <Section title={t('invoices.title')}>
          {list.length === 0 ? (
            <EmptyState
              icon={<Receipt size={22} />}
              title={t('invoices.emptyYear', { year })}
              text={lease ? t('invoices.emptyText') : t('invoices.noLease')}
            />
          ) : (
            <>
              {overdue.length > 1 && (
                <div className={s.bulk}>
                  <span>{t('invoices.overdueCount', { count: overdue.length })}</span>
                  <Button onClick={() => setConfirmSettle(true)}>{t('invoices.settleAll')}</Button>
                </div>
              )}
              <Panel>
                <InvoiceList invoices={list} propertyId={p.id} manage={!p.archivedAt} />
              </Panel>
            </>
          )}
        </Section>
      )}
      {part === 'invoices' && lease && <Templates lease={lease} archived={Boolean(p.archivedAt)} />}

      {part === 'expenses' && (
        <>
          <ExpensesSection
            propertyId={p.id}
            year={year}
            expenses={expenseData?.expenses ?? []}
            archived={Boolean(p.archivedAt)}
          />
          <RecurringExpenses propertyId={p.id} archived={Boolean(p.archivedAt)} />
        </>
      )}

      <TaxNote />

      <Dialog
        open={confirmSettle}
        onClose={() => setConfirmSettle(false)}
        title={t('invoices.settleAll')}
      >
        <p>{t('invoices.settleText', { count: overdue.length })}</p>
        {settle.isError && <Alert>{t(errorKey(settle.error))}</Alert>}
        <DialogActions>
          <Button variant="ghost" onClick={() => setConfirmSettle(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={settle.isPending}
            onClick={() => settle.mutate(undefined, { onSuccess: () => setConfirmSettle(false) })}
          >
            {t('invoices.settleConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

function Templates({ lease, archived }: { lease: Lease; archived: boolean }) {
  const { t, i18n } = useTranslation();
  const { data: templates, isLoading } = useInvoiceTemplates(lease.id);
  const remove = useDeleteTemplate(lease.propertyId);
  const [creating, setCreating] = useState(false);
  const [removing, setRemoving] = useState<InvoiceTemplate | null>(null);
  const [editingTpl, setEditingTpl] = useState<InvoiceTemplate | null>(null);
  if (isLoading) return null;
  const list = templates ?? [];
  const canAdd = !archived && lease.status === 'active';
  const range = (tpl: InvoiceTemplate) =>
    `${monthLabel(tpl.startMonth, i18n.language)} – ${
      tpl.endMonth ? monthLabel(tpl.endMonth, i18n.language) : t('invoices.untilFurther')
    }`;

  return (
    <Section
      title={t('invoices.templates')}
      note={t('invoices.templatesNote')}
      action={
        canAdd && list.length > 0 ? (
          <Button variant="ghost" onClick={() => setCreating(true)}>
            <Plus size={15} aria-hidden="true" />
            {t('invoices.newTemplate')}
          </Button>
        ) : undefined
      }
    >
      {list.length === 0 ? (
        <EmptyState
          icon={<Receipt size={22} />}
          title={t('invoices.noTemplate')}
          text={t('invoices.noTemplateText')}
          action={
            canAdd ? (
              <Button variant="primary" onClick={() => setCreating(true)}>
                {t('invoices.createTemplate')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Panel>
          {[...list].reverse().map((tpl) => (
            <div key={tpl.id} className={s.templateRow}>
              <div className={s.main}>
                <span className={s.month}>{range(tpl)}</span>
                <span className={s.meta}>
                  {t('lease.perMonth', { amount: formatCents(tpl.totalCents, i18n.language) })} ·{' '}
                  {t('lease.dueDayValue', { day: tpl.dueDay })} ·{' '}
                  {tpl.format === 'app' ? (
                    t('invoices.formatApp')
                  ) : tpl.fileId ? (
                    <a href={fileUrl(tpl.fileId)} target="_blank" rel="noreferrer">
                      {tpl.fileName ?? t('invoices.formatPdf')}
                    </a>
                  ) : (
                    t('invoices.formatPdf')
                  )}
                </span>
                <span className={s.meta}>
                  {tpl.lines
                    .map((l) => `${l.description} ${formatCents(l.amountCents, i18n.language)}`)
                    .join(' · ')}
                </span>
              </div>
              {!archived && (
                <div className={s.side}>
                  <Button
                    variant="ghost"
                    icon
                    aria-label={t('invoices.editTemplate')}
                    onClick={() => setEditingTpl(tpl)}
                  >
                    <Pencil size={15} aria-hidden="true" />
                  </Button>
                  {tpl.removable && (
                    <Button
                      variant="ghost"
                      icon
                      aria-label={t('common.delete')}
                      onClick={() => setRemoving(tpl)}
                    >
                      <Trash2 size={15} aria-hidden="true" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </Panel>
      )}
      {editingTpl && (
        <EditTemplateDialog
          lease={lease}
          template={editingTpl}
          onClose={() => setEditingTpl(null)}
        />
      )}
      {creating && (
        <TemplateDialog lease={lease} templates={list} onClose={() => setCreating(false)} />
      )}
      <Dialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title={t('invoices.removeTemplate')}
      >
        <p>{t('invoices.removeTemplateText')}</p>
        <DialogActions>
          <Button variant="ghost" onClick={() => setRemoving(null)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={remove.isPending}
            onClick={() => remove.mutate(removing!.id, { onSuccess: () => setRemoving(null) })}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Section>
  );
}

/**
 * Pohjan muokkaus: eräpäivä ja viimeisimmän pohjan päättymiskuukausi. Summat muutetaan
 * uudella pohjalla, jotta vanhat laskut pysyvät ennallaan ja muutoskuukausi näkyy lokissa.
 */
function EditTemplateDialog({
  lease,
  template,
  onClose,
}: {
  lease: Lease;
  template: InvoiceTemplate;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const update = useUpdateTemplate(lease.propertyId);
  const [end, setEnd] = useState(template.endMonth ?? '');
  const [dueDay, setDueDay] = useState(String(template.dueDay));
  const latest = lease.endDate ? lease.endDate.slice(0, 7) : addMonths(currentMonth(), 36);
  const months: string[] = [];
  for (let m = template.startMonth; m <= latest; m = addMonths(m, 1)) months.push(m);

  function submit(event: FormEvent) {
    event.preventDefault();
    update.mutate(
      { id: template.id, body: { endMonth: end || null, dueDay: Number(dueDay) } },
      { onSuccess: onClose },
    );
  }

  const error =
    update.error instanceof ApiError && update.error.code === 'paid_after_end'
      ? t('invoices.paidAfterEnd')
      : update.isError
        ? t(errorKey(update.error))
        : null;

  return (
    <Dialog open onClose={onClose} title={t('invoices.editTemplate')}>
      <form onSubmit={submit} noValidate style={{ display: 'grid', gap: '1.1rem' }}>
        <p className={s.meta}>{t('invoices.editTemplateHint')}</p>
        <div className={ps.formRow}>
          <SelectField
            label={t('invoices.to')}
            value={end}
            disabled={!template.latest}
            hint={template.latest ? undefined : t('invoices.endFixed')}
            onChange={(e) => setEnd(e.target.value)}
          >
            <option value="">{t('invoices.untilFurther')}</option>
            {months.map((m) => (
              <option key={m} value={m}>
                {monthLabel(m, i18n.language)}
              </option>
            ))}
          </SelectField>
          <SelectField
            label={t('lease.dueDay')}
            value={dueDay}
            onChange={(e) => setDueDay(e.target.value)}
          >
            {Array.from({ length: 31 }, (_, i) => String(i + 1)).map((d) => (
              <option key={d} value={d}>
                {t('lease.dueDayValue', { day: d })}
              </option>
            ))}
          </SelectField>
        </div>
        {error && <Alert>{error}</Alert>}
        <DialogActions>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={update.isPending}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * Uusi laskupohja. Ensimmäinen pohja alkaa oletuksena vuoden alusta (tai vuokrasuhteen
 * alusta), jolloin kuluvan vuoden kirjanpito on heti kokonainen. Myöhempi pohja alkaa
 * seuraavasta kuukaudesta, esim. vuokrankorotuksesta.
 */
function TemplateDialog({
  lease,
  templates,
  onClose,
}: {
  lease: Lease;
  templates: InvoiceTemplate[];
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  const { data: occupants } = useOccupants(lease.id);
  const create = useCreateTemplate(lease.propertyId, lease.id);
  const upload = useUploadFiles(lease.propertyId);

  const last = templates.at(-1);
  const yearStart = `${new Date().getFullYear()}-01`;
  const leaseStart = lease.startDate.slice(0, 7);
  const earliest = last
    ? addMonths(last.startMonth, 1)
    : leaseStart > yearStart
      ? leaseStart
      : yearStart;
  const latest = lease.endDate ? lease.endDate.slice(0, 7) : addMonths(currentMonth(), 24);
  const months: string[] = [];
  for (let m = earliest; m <= latest; m = addMonths(m, 1)) months.push(m);

  const persons =
    lease.parties.filter((x) => x.role === 'tenant').length +
    (occupants ?? []).filter((o) => o.kind === 'person').length;
  const initialLines = last
    ? last.lines
    : [
        { description: t('invoices.rentLine'), amountCents: lease.rentCents },
        ...(lease.waterBilling === 'per_person' && lease.waterCents !== null
          ? [
              {
                description: t('invoices.waterLine', { count: persons }),
                amountCents: lease.waterCents * persons,
              },
            ]
          : []),
      ];

  const [start, setStart] = useState(
    last
      ? months.includes(addMonths(currentMonth(), 1))
        ? addMonths(currentMonth(), 1)
        : earliest
      : earliest,
  );
  const [end, setEnd] = useState('');
  const [dueDay, setDueDay] = useState(String(last?.dueDay ?? lease.dueDay));
  const [format, setFormat] = useState<InvoiceFormat>(last?.format ?? 'app');
  const [iban, setIban] = useState(me?.iban ? formatIban(me.iban) : '');
  const [file, setFile] = useState<File | null>(null);
  const [lines, setLines] = useState<DraftLine[]>(toDraft(initialLines, i18n.language));
  const [tried, setTried] = useState(false);
  const [error, setError] = useState<string>();
  const parsed = parseLines(lines);
  const busy = create.isPending || upload.isPending;
  const ibanOk = isValidIban(iban);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setTried(true);
    setError(undefined);
    if (!parsed || (format === 'pdf' && !file) || (format === 'app' && !ibanOk)) return;
    try {
      let fileId: string | null = null;
      if (format === 'pdf' && file) {
        const [stored] = await upload.mutateAsync({
          files: [file],
          folder: 'invoice',
          shared: true,
        });
        fileId = stored?.id ?? null;
      }
      await create.mutateAsync({
        startMonth: start,
        endMonth: end || null,
        dueDay: Number(dueDay),
        format,
        lines: parsed,
        fileId,
        iban: format === 'app' ? iban : null,
      });
      onClose();
    } catch (e) {
      setError(errorKey(e));
    }
  }

  return (
    <Dialog open onClose={onClose} title={t('invoices.newTemplate')} wide>
      <form onSubmit={submit} noValidate style={{ display: 'grid', gap: '1.1rem' }}>
        <Notice>{last ? t('invoices.nextTemplateIntro') : t('invoices.firstTemplateIntro')}</Notice>
        <div className={ps.formRow}>
          <SelectField
            label={t('invoices.from')}
            value={start}
            onChange={(e) => setStart(e.target.value)}
          >
            {months.map((m) => (
              <option key={m} value={m}>
                {monthLabel(m, i18n.language)}
              </option>
            ))}
          </SelectField>
          <SelectField
            label={t('invoices.to')}
            value={end}
            onChange={(e) => setEnd(e.target.value)}
          >
            <option value="">{t('invoices.untilFurther')}</option>
            {months
              .filter((m) => m >= start)
              .map((m) => (
                <option key={m} value={m}>
                  {monthLabel(m, i18n.language)}
                </option>
              ))}
          </SelectField>
          <SelectField
            label={t('lease.dueDay')}
            value={dueDay}
            onChange={(e) => setDueDay(e.target.value)}
          >
            {Array.from({ length: 31 }, (_, i) => String(i + 1)).map((d) => (
              <option key={d} value={d}>
                {t('lease.dueDayValue', { day: d })}
              </option>
            ))}
          </SelectField>
        </div>
        <LinesEditor lines={lines} onChange={setLines} showErrors={tried} />
        <ChoiceGroup
          name="invoice-format"
          legend={t('invoices.format')}
          value={format}
          onChange={setFormat}
          options={[
            { value: 'app', title: t('invoices.formatApp'), text: t('invoices.formatAppText') },
            { value: 'pdf', title: t('invoices.formatPdf'), text: t('invoices.formatPdfText') },
          ]}
        />
        {format === 'app' ? (
          <TextField
            label={t('account.iban')}
            hint={t('invoices.ibanHint')}
            autoComplete="off"
            value={iban}
            onChange={(e) => setIban(e.target.value)}
            error={tried && !ibanOk ? t('errors.iban') : undefined}
            style={{ maxWidth: 320 }}
          />
        ) : (
          <TextField
            label={t('invoices.pdfFile')}
            hint={t('invoices.pdfHint')}
            type="file"
            accept="application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            error={tried && !file ? t('errors.required') : undefined}
          />
        )}
        {error && <Alert>{t(error)}</Alert>}
        <DialogActions>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={busy || months.length === 0}>
            {t('invoices.saveTemplate')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
